import { useEffect, useState } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

/**
 * Мелкие элементы интерфейса. Классы повторяют оформление из HTML-прототипов,
 * чтобы кабинеты выглядели ровно так же, как согласованные макеты.
 */

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' }) {
  const styles = {
    primary: 'bg-accent text-white hover:bg-accent-hover disabled:bg-accent/40',
    secondary: 'bg-surface-2 text-ink border border-border hover:bg-border',
    danger: 'bg-bad-bg text-bad border border-bad-border',
  }[variant];

  return (
    <button
      {...props}
      className={`rounded-[10px] px-[18px] py-[10.5px] text-[13.5px] font-bold transition-colors disabled:cursor-not-allowed ${styles} ${className}`}
    />
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="mb-3.5 block">
      <span className="mb-1.5 block text-[12.5px] font-bold text-ink-dim">
        {label}
        {hint && <span className="ml-1.5 font-semibold text-ink-faint">{hint}</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-[12px] font-semibold text-bad">{error}</span>}
    </label>
  );
}

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-[9px] border-[1.5px] border-border bg-surface px-3 py-2.5 text-[13.5px] outline-none focus:border-accent ${className}`}
    />
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-border bg-surface shadow-card ${className}`}>
      {children}
    </div>
  );
}

/** Плашка ошибки над формой: текст приходит из кода ошибки API. */
export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3.5 rounded-[10px] border border-bad-border bg-bad-bg px-3.5 py-2.5 text-[12.5px] font-semibold text-bad">
      {children}
    </div>
  );
}

/**
 * Знак без подписи. В шапке кабинета названия нет: человек и так знает,
 * куда вошёл, а строка занимала место каждый рабочий день.
 */
export function BrandMark() {
  return (
    <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[9px] bg-gradient-to-br from-accent to-brand-2 text-[14px] font-extrabold text-white">
      A
    </span>
  );
}

/** Знак с названием — для страниц, куда человек попадает по ссылке извне. */
export function Brand({ subtitle }: { subtitle?: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <BrandMark />
      <span className="text-[15px] font-extrabold tracking-tight">Aurea HR</span>
      {subtitle && <span className="text-[12.5px] text-ink-faint">{subtitle}</span>}
    </div>
  );
}

/**
 * Выбор времени часами и минутами.
 *
 * Раньше время вводили строкой, и «9:00», «09.00» или «девять» одинаково
 * не проходили проверку — форма отвечала отказом уже после отправки.
 * Два списка ошибиться не дают вовсе.
 *
 * Минуты идут через пять: встречи назначают на круглое время, а список
 * из шестидесяти значений листать неудобно.
 */
export function TimeSelect({
  value,
  onChange,
  fromHour = 8,
  toHour = 21,
}: {
  value: string;
  onChange: (value: string) => void;
  fromHour?: number;
  toHour?: number;
}) {
  /*
   * Половинки хранятся здесь, а не выводятся из value.
   *
   * Наружу время отдаётся только целиком: пока выбрана одна половина, там
   * пусто, чтобы кнопка оставалась заблокированной. Если при этом читать
   * списки из того же пустого значения, первый же выбор сбрасывается назад
   * на «чч» — и до второго дело не доходит.
   */
  const [hour, setHour] = useState(() => value.split(':')[0] ?? '');
  const [minute, setMinute] = useState(() => value.split(':')[1] ?? '');

  // Значение сменили снаружи — например, форма очистилась после сохранения.
  useEffect(() => {
    const [nextHour = '', nextMinute = ''] = value.split(':');
    setHour(nextHour);
    setMinute(nextMinute);
  }, [value]);

  const hours = Array.from({ length: toHour - fromHour + 1 }, (_, i) =>
    String(fromHour + i).padStart(2, '0'),
  );
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

  const set = (nextHour: string, nextMinute: string) => {
    setHour(nextHour);
    setMinute(nextMinute);
    onChange(nextHour && nextMinute ? `${nextHour}:${nextMinute}` : '');
  };

  const style =
    'rounded-[9px] border-[1.5px] border-border bg-surface px-2.5 py-2.5 text-[13.5px] outline-none focus:border-accent';

  return (
    <div className="flex items-center gap-1.5">
      <select
        aria-label="Часы"
        className={style}
        value={hour}
        onChange={(event) => set(event.target.value, minute)}
      >
        <option value="">чч</option>
        {hours.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>

      <span className="font-bold text-ink-faint">:</span>

      <select
        aria-label="Минуты"
        className={style}
        value={minute}
        onChange={(event) => set(hour, event.target.value)}
      >
        <option value="">мм</option>
        {minutes.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    </div>
  );
}
